import { Injectable } from '@angular/core';
import { StateType } from 'src/app/core/models/StateType';

const tabs: string[] = [
  '/charity/charity-info',
  '/charity/newsfeed',
  '/charity/events',
  '/charity/commerce',
  '/main/home',
  '/main/discover',
  '/main/sunny-drives',
  '/main/commerce',
  '/business/business-info',
  '/business/shopify-management',
  '/business/social-content',
  '/business/items',
];

@Injectable({
  providedIn: 'root',
})
export class TabService {
  private userTabHistory: { [key: string]: { url: string; state: StateType }[] } = {
    'home':
      [{ url: '/main/home', state: {} }],
    'discover':
      [{ url: '/main/discover', state: {} }],
    'sunny-drives':
      [{ url: '/main/sunny-drives', state: {} }],
    'commerce':
      [{ url: '/main/commerce', state: {} }],
  };
  private charityTabHistory: { [key: string]: { url: string; state: StateType }[] } = {
    'charity-info':
      [{ url: '/charity/charity-info', state: {} }],
    'newsfeed':
      [{ url: '/charity/newsfeed', state: {} }],
    'events':
      [{ url: '/charity/events', state: {} }],
    'commerce':
      [{ url: '/charity/commerce', state: {} }],
  };
  private businessTabHistory: { [key: string]: { url: string; state: StateType }[] } = {
    'business-info': 
      [{ url: '/business/business-info', state: {} }],
    'shopify-management':
      [{ url: '/business/shopify-management', state: {} }],
    'social-content': 
      [{ url: '/business/social-content', state: {} }],
    'items': 
      [{ url: '/business/items', state: {} }],
  };
  private lastTab: { user: string; charity: string; business: string } = { user: 'home', charity: 'charity-info', business: 'business-info' };

  constructor() {}

  trackNavigation(portal: 'user' | 'charity' | 'business', tab: string, url: string, state: StateType): void {
    if (!url.startsWith('/main') && portal === 'user') return;
    if (!url.startsWith('/charity') && portal === 'charity') return;
    if (!url.startsWith('/business') && portal === 'business') return;
    if (tabs.includes(url)) return;

    const history: { [key: string]: { url: string; state: StateType }[] } = this.getHistoryForPortal(portal);
    
    // Ensure the tab exists in history
    if (!history[tab]) {
      const rootUrl: string = portal === 'user' ? `/main/${tab}` : 
                              portal === 'charity' ? `/charity/${tab}` : 
                              `/business/${tab}`;
      history[tab] = [{ url: rootUrl, state: {} }];
    }
    
    const tabHistory: { url: string; state: StateType }[] = history[tab];
    if (tabHistory.length === 0 || tabHistory[tabHistory.length - 1]?.url !== url) {
      tabHistory.push({ url, state });
      history[tab] = tabHistory;
    }
    this.lastTab[portal] = tab;
  }

  getLastTab(portal: 'user' | 'charity' | 'business'): string {
    return this.lastTab[portal];
  }

  getLastPage(portal: 'user' | 'charity' | 'business'): { url: string; state: StateType } | null {
    const lastTab: string = this.lastTab[portal];
    const history: { [key: string]: { url: string; state: StateType }[] } = this.getHistoryForPortal(portal);
    return history[lastTab]?.[history[lastTab].length - 1] || null;
  }

  getLastTabPage(portal: 'user' | 'charity' | 'business', tab: string): { url: string; state: StateType } | null {
    const history: { [key: string]: { url: string; state: StateType }[] } = this.getHistoryForPortal(portal);
    return history[tab]?.[history[tab].length - 1] || null;
  }

  navigateBack(portal: 'user' | 'charity' | 'business', tab: string): { url: string; state: StateType } | null {
    const history: { [key: string]: { url: string; state: StateType }[] } = this.getHistoryForPortal(portal);
    if (history[tab]?.length > 1) history[tab].pop();
    return history[tab]?.[history[tab].length - 1] || null;
  }

  resetTabHistory(portal: 'user' | 'charity' | 'business', tab: string): void {
    const history: { [key: string]: { url: string; state: StateType }[] } = this.getHistoryForPortal(portal);
    const rootUrl: string = portal === 'user' ? `/main/${tab}` : 
                            portal === 'charity' ? `/charity/${tab}` : 
                            `/business/${tab}`;
  
    history[tab] = [{ url: rootUrl, state: {} }];
  }

  private getHistoryForPortal(portal: 'user' | 'charity' | 'business'): { [key: string]: { url: string; state: StateType }[] } {
    if (portal === 'user') {
      return this.userTabHistory;
    } else if (portal === 'charity') {
      return this.charityTabHistory;
    } else {
      return this.businessTabHistory;
    }
  }

  clearAllHistory(): void {
    this.userTabHistory = {
      'home':
        [{ url: '/main/home', state: {} }],
      'discover':
        [{ url: '/main/discover', state: {} }],
      'sunny-drives':
        [{ url: '/main/sunny-drives', state: {} }],
      'commerce':
        [{ url: '/main/commerce', state: {} }],
    };
    this.charityTabHistory = {
      'charity-info':
        [{ url: '/charity/charity-info', state: {} }],
      'newsfeed':
        [{ url: '/charity/newsfeed', state: {} }],
      'events':
        [{ url: '/charity/events', state: {} }],
      'commerce':
        [{ url: '/charity/commerce', state: {} }],
    };
    this.businessTabHistory = {
      'business-info':
        [{ url: '/business/business-info', state: {} }],
      'shopify-management':
        [{ url: '/business/shopify-management', state: {} }],
      'social-content':
        [{ url: '/business/social-content', state: {} }],
      'items':
        [{ url: '/business/items', state: {} }],
    };
    this.lastTab = { user: 'home', charity: 'charity-info', business: 'business-info' };
  }

  setLastTab(portal: 'user' | 'charity' | 'business', tab: string): void {
    this.lastTab[portal] = tab;
  }

  getPostAuthRoute(): string {
    const isNewUser: string | null = localStorage.getItem('new-user');
    if (isNewUser === 'no') {
      return '/main/home';
    }
    localStorage.setItem('new-user', 'no');
    this.setLastTab('user', 'discover');
    return '/main/discover';
  }

  getPostAuthRouteWithReturnUrl(): string {
    const returnUrl: string | null = sessionStorage.getItem('returnUrl');

    if (returnUrl && this.isValidReturnUrl(returnUrl)) {
      sessionStorage.removeItem('returnUrl');
      localStorage.setItem('new-user', 'no');
      this.setTabContextFromUrl(returnUrl);
      return returnUrl;
    }

    return this.getPostAuthRoute();
  }

  isValidReturnUrl(url: string): boolean {
    return url.startsWith('/charity/') ||
           url.startsWith('/main/') ||
           url.startsWith('/business/');
  }

  setTabContextFromUrl(url: string): void {
    if (url.startsWith('/charity/')) {
      const segments: string[] = url.split('/').filter(s => s);
      if (segments.length >= 2) {
        const page: string = segments[1];
        const tabMapping: { [key: string]: string } = {
          'charity-orders': 'commerce',
          'charity-order-detail': 'commerce',
          'marketplace': 'commerce',
          'auctions': 'commerce',
          'commerce': 'commerce',
          'donation-history': 'charity-info',
          'newsfeed': 'newsfeed',
          'events': 'events',
          'event-detail': 'events',
          'followers': 'charity-info',
          'impact-points': 'charity-info',
          'fund-a-needs': 'charity-info',
          'charity-notifications': 'charity-info',
        };
        const tab: string = tabMapping[page] || 'charity-info';
        this.setLastTab('charity', tab);
      }
    } else if (url.startsWith('/business/')) {
      const segments: string[] = url.split('/').filter(s => s);
      if (segments.length >= 2) {
        const page: string = segments[1];
        const tabMapping: { [key: string]: string } = {
          'order-detail': 'items',
          'orders': 'items',
          'items': 'items',
          'items-grid': 'items',
          'item-detail': 'items',
          'social-content': 'social-content',
          'shopify-management': 'shopify-management',
          'notifications': 'business-info',
        };
        const tab: string = tabMapping[page] || 'business-info';
        this.setLastTab('business', tab);
      }
    } else if (url.startsWith('/main/')) {
      const segments: string[] = url.split('/').filter(s => s);
      if (segments.length >= 2) {
        const page: string = segments[1];
        const tabMapping: { [key: string]: string } = {
          'user-orders': 'commerce',
          'user-order-detail': 'commerce',
          'recurring-donations': 'home',
          'notifications': 'home',
          'credits': 'home',
          'home': 'home',
          'discover': 'discover',
          'sunny-drives': 'sunny-drives',
          'commerce': 'commerce',
        };
        const tab: string = tabMapping[page] || 'home';
        this.setLastTab('user', tab);
      }
    }
  }
}
